import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);
  return <>
      <footer className="py-12 bg-[#0B1029] border-t border-white/5" data-id="element-54">
        <div className="container mx-auto px-6 max-w-6xl" data-id="element-55">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-56">
            <p className="text-slate-500 text-sm font-light" data-id="element-57">
              © {new Date().getFullYear()} The 3:00 AM Sleep Fix. All rights
              reserved.
            </p>
            <div className="flex items-center gap-8" data-id="element-58">
              <button onClick={() => setOpenModal('privacy')} className="text-slate-400 hover:text-[#F5D0A9] text-sm font-light transition-colors" data-id="element-59">
                Privacy Policy
              </button>
              <button onClick={() => setOpenModal('terms')} className="text-slate-400 hover:text-[#F5D0A9] text-sm font-light transition-colors" data-id="element-60">
                Terms of Service
              </button>
            </div>
          </div>
          <p className="text-slate-600 text-xs font-light text-center mt-8 max-w-3xl mx-auto leading-relaxed" data-id="element-61">
            Disclaimer: This audiobook is for educational purposes only and is
            not a substitute for medical advice. Always consult a qualified
            healthcare provider regarding sleep issues or hormonal concerns.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-62">
        {openModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-black/70 backdrop-blur-sm" onClick={() => setOpenModal(null)} role="dialog" aria-modal="true" data-id="element-63">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} transition={{
          duration: 0.3,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="bg-[#111836] rounded-3xl border border-white/10 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-64">
              <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/5" data-id="element-65">
                <h3 className="text-2xl md:text-3xl font-serif text-white" data-id="element-66">
                  {openModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h3>
                <button onClick={() => setOpenModal(null)} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-white/5 transition-colors" aria-label="Close" data-id="element-67">
                  <X className="w-5 h-5" data-id="element-68" />
                </button>
              </div>
              <div className="overflow-y-auto p-6 md:p-8 text-slate-300 font-light leading-relaxed space-y-5" data-id="element-69">
                {openModal === 'privacy' ? <PrivacyContent data-id="element-70" /> : <TermsContent data-id="element-71" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-sm text-slate-500" data-id="element-72">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-73">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-74">
          1. Information We Collect
        </h4>
        <p data-id="element-75">
          When you purchase The 3:00 AM Sleep Fix, we collect basic information
          necessary to deliver your digital product, including your name and
          payment details processed securely through our payment provider.
        </p>
      </section>

      <section data-id="element-76">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-77">
          2. How We Use Your Information
        </h4>
        <p data-id="element-78">
          Your information is used solely to fulfill your order, provide access
          to the audiobook content, and send essential transactional
          communications related to your purchase.
        </p>
      </section>

      <section data-id="element-79">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-80">
          3. Data Protection
        </h4>
        <p data-id="element-81">
          We implement industry-standard security measures to protect your
          personal information. All transactions are processed through
          encrypted, PCI-compliant payment systems. We do not store your payment
          card details on our servers.
        </p>
      </section>

      <section data-id="element-82">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-83">
          4. Third-Party Services
        </h4>
        <p data-id="element-84">
          We use trusted third-party services for payment processing and content
          delivery. These providers are bound by their own privacy policies and
          security standards.
        </p>
      </section>

      <section data-id="element-85">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-86">5. Cookies</h4>
        <p data-id="element-87">
          We use minimal cookies to ensure the website functions properly and to
          analyze usage patterns for improvement purposes.
        </p>
      </section>

      <section data-id="element-88">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-89">
          6. Your Rights
        </h4>
        <p data-id="element-90">
          You have the right to access, correct, or request deletion of your
          personal data at any time, subject to our legal obligations to retain
          certain transaction records.
        </p>
      </section>

      <section data-id="element-91">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-92">
          7. Changes to This Policy
        </h4>
        <p data-id="element-93">
          We may update this privacy policy from time to time. Any changes will
          be reflected on this page with an updated revision date.
        </p>
      </section>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-sm text-slate-500" data-id="element-94">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-95">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-96">
          1. Acceptance of Terms
        </h4>
        <p data-id="element-97">
          By purchasing and using The 3:00 AM Sleep Fix audiobook, you agree to
          be bound by these Terms of Service. If you do not agree to these
          terms, please do not purchase or use the product.
        </p>
      </section>

      <section data-id="element-98">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-99">
          2. Educational Purpose Only
        </h4>
        <p data-id="element-100">
          The content provided in this audiobook is for informational and
          educational purposes only. It is not intended to be a substitute for
          professional medical advice, diagnosis, or treatment. Always consult a
          qualified healthcare provider regarding any sleep, hormonal, or health
          concerns.
        </p>
      </section>

      <section data-id="element-101">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-102">
          3. License and Use
        </h4>
        <p data-id="element-103">
          Upon purchase, you receive a non-transferable, personal license to
          access and use the audiobook content for your own private use.
          Redistribution, resale, public broadcast, or sharing of the content is
          strictly prohibited.
        </p>
      </section>

      <section data-id="element-104">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-105">
          4. Intellectual Property
        </h4>
        <p data-id="element-106">
          All content, including audio recordings, written materials, and
          accompanying resources, is the intellectual property of The 3:00 AM
          Sleep Fix and is protected by applicable copyright laws.
        </p>
      </section>

      <section data-id="element-107">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-108">
          5. No Guarantees
        </h4>
        <p data-id="element-109">
          While the protocol is based on established biological principles,
          individual results may vary. We make no guarantees regarding specific
          outcomes, sleep improvement, or health benefits.
        </p>
      </section>

      <section data-id="element-110">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-111">
          6. Refund Policy
        </h4>
        <p data-id="element-112">
          Due to the digital nature of this product, all sales are final upon
          delivery of access. We encourage you to carefully review the product
          description before purchasing.
        </p>
      </section>

      <section data-id="element-113">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-114">
          7. Limitation of Liability
        </h4>
        <p data-id="element-115">
          To the maximum extent permitted by law, we shall not be liable for any
          indirect, incidental, or consequential damages arising from your use
          of this product.
        </p>
      </section>

      <section data-id="element-116">
        <h4 className="text-lg font-medium text-[#F5D0A9] mb-2" data-id="element-117">
          8. Modifications
        </h4>
        <p data-id="element-118">
          We reserve the right to modify these terms at any time. Continued use
          of the product following any changes constitutes acceptance of the
          revised terms.
        </p>
      </section>
    </>;
}